/**
 * feat-270 — "is the curriculum approval gate on for this tenant?", available to
 * any panel in the editor without threading a boolean through four component
 * layers.
 *
 * WHY A CONTEXT AND NOT A PROP. The consumers are deep and scattered —
 * `SaveStatus` sits inside `ItemDetailFooter`, which is rendered separately by
 * `QuestionDetailForm`, `ReadingDetailForm`, `VideoDetailForm` and
 * `QuizContentManagement` itself. Threading a prop would touch all four call
 * sites and every component between, and every one of those edits is a chance to
 * miss one — a panel that silently keeps today's footer while the others gain the
 * line is a worse outcome than either state applied consistently.
 *
 * **The default is `false`, and that is AT-29 by construction.** A tree with no
 * provider — every existing test, and every flag-off tenant — renders exactly
 * today's editor. The approval surface has to be switched ON by a provider that
 * only appears when the server says `approval_required: true`; it cannot be left
 * on by omission.
 */

import React, { createContext, useContext, useMemo } from 'react';

export interface ApprovalGate {
  /** ADR-B §3.1 `approval_required`. False ⇒ the whole surface is absent. */
  approvalRequired: boolean;
  /** ADR-B §3.1 `can_approve` — a RENDERING hint. The guard is the API's 403. */
  canApprove: boolean;
}

const OFF: ApprovalGate = { approvalRequired: false, canApprove: false };

const ApprovalGateContext = createContext<ApprovalGate>(OFF);

export const ApprovalGateProvider: React.FC<{
  approvalRequired: boolean;
  canApprove: boolean;
  children: React.ReactNode;
}> = ({ approvalRequired, canApprove, children }) => {
  const value = useMemo(
    () => ({ approvalRequired, canApprove }),
    [approvalRequired, canApprove],
  );
  return (
    <ApprovalGateContext.Provider value={value}>{children}</ApprovalGateContext.Provider>
  );
};

/** Defaults to the gate being OFF, so absence of a provider is today's editor. */
export function useApprovalGate(): ApprovalGate {
  return useContext(ApprovalGateContext);
}
